import { assertPackageVersion } from '@dynamic-labs-sdk/assert-package-version';

import {
  name as packageName,
  version as packageVersion,
} from '../../package.json';
assertPackageVersion(packageName, packageVersion);

/**
 * @categoryDescription Extension Development
 * Core utilities and functions for building Dynamic SDK extensions.
 * These APIs provide low-level access to the SDK internals and are intended
 * for advanced use cases and extension development. Most applications should
 * use the higher-level APIs exported from the main SDK package instead.
 */

/** @category Extension Development */
export { getCore } from '../client/core/getCore';
/** @category Extension Development */
export type { DynamicCore, DynamicCoreState } from '../client/core/types';
/** @category Extension Development */
export { getDefaultClient } from '../client/defaultClient';
/** @category Extension Development */
export { APIError } from '../errors/APIError';
/** @category Extension Development */
export { CannotTrackError } from '../errors/CannotTrackError';
/** @category Extension Development */
export { ClientNotFoundError } from '../errors/ClientNotFoundError';
/** @category Extension Development */
export { ClientsDoNotMatchError } from '../errors/ClientsDoNotMatchError';
/** @category Extension Development */
export { InvalidStorageSet } from '../errors/InvalidStorageSet';
/** @category Extension Development */
export { InvalidWalletProviderKeyError } from '../errors/InvalidWalletProviderKeyError';
/** @category Extension Development */
export { MethodNotImplementedError } from '../errors/MethodNotImplementedError';
/** @category Extension Development */
export { MultipleClientsFoundError } from '../errors/MultipleClientsFoundError';
/** @category Extension Development */
export { NoWalletProviderFoundError } from '../errors/NoWalletProviderFoundError';
/** @category Extension Development */
export { ValueMustBeDefinedError } from '../errors/ValueMustBeDefinedError';
/** @category Extension Development */
export { createApiClient, DYNAMIC_SDK_API_VERSION } from '../modules/apiClient';
/** @category Extension Development */
export { createVisit } from '../modules/auth/createVisit';
/** @category Extension Development */
export { updateAuthFromVerifyResponse } from '../modules/auth/updateAuthFromVerifyResponse';
/** @category Extension Development */
export { consumeCaptchaToken } from '../modules/captcha/consumeCaptchaToken';
/** @category Extension Development */
export { setCaptchaToken } from '../modules/captcha/setCaptchaToken';
/** @category Extension Development */
export { emitEvent } from '../modules/clientEvents';
/** @category Extension Development */
export { hasExtension } from '../modules/extension/hasExtension';
/** @category Extension Development */
export { registerExtension } from '../modules/extension/registerExtension';
/** @category Extension Development */
export { waitForProjectSettings } from '../modules/initializeClient/waitForProjectSettings';
/** @category Extension Development */
export { consumeMfaTokenIfRequiredForAction } from '../modules/mfa/consumeMfaTokenIfRequiredForAction';
/** @category Extension Development */
export { isCookieEnabled } from '../modules/projectSettings/isCookieEnabled';
/** @category Extension Development */
export { generateSessionKeys } from '../modules/sessionKeys/generateSessionKeys/generateSessionKeys';
/** @category Extension Development */
export { getSessionKeys } from '../modules/sessionKeys/getSessionKeys';
/** @category Extension Development */
export { getSignedSessionId } from '../modules/sessionKeys/getSignedSessionId';
/** @category Extension Development */
export type { SessionKeys } from '../modules/sessionKeys/sessionKeys.types';
/** @category Extension Development */
export { CHAINS_INFO_MAP } from '../modules/wallets/constants';
/** @category Extension Development */
export { emitWalletAccountsChangedEvent } from '../modules/wallets/emitWalletAccountsChangedEvent';
/** @category Extension Development */
export { getWalletProviders } from '../modules/wallets/getWalletProviders';
/** @category Extension Development */
export { createBaseNetworkProvider } from '../modules/wallets/networks/createBaseNetworkProvider';
/** @category Extension Development */
export { getNetworkDataForNetworkId } from '../modules/wallets/networks/getNetworkDataForNetworkId';
/** @category Extension Development */
export { getNetworkProviderFromNetworkId } from '../modules/wallets/networks/getNetworkProviderFromNetworkId';
/** @category Extension Development */
export { getNetworkProviders } from '../modules/wallets/networks/getNetworkProviders';
/** @category Extension Development */
export { getLastKnownNetworkRegistry } from '../modules/wallets/networks/lastKnownNetworkRegistry/getLastKnownNetworkRegistry';
/** @category Extension Development */
export type { LastKnownNetworkRegistry } from '../modules/wallets/networks/lastKnownNetworkRegistry/LastKnownNetworkRegistry.types';
/** @category Extension Development */
export type { BaseNetworkProvider } from '../modules/wallets/networks/networkProvider/networkProvider.types';
/** @category Extension Development */
export {
  getNetworkProviderBuilderRegistry,
  type NetworkProviderBuilder,
  type NetworkProviderBuilderRegistry,
} from '../modules/wallets/networks/networkProviderBuilderRegistry';
/** @category Extension Development */
export { removeUnverifiedWalletAccount } from '../modules/wallets/unverifiedWalletAccounts/removeUnverifiedWalletAccount';
export { setUnverifiedWalletAccounts } from '../modules/wallets/unverifiedWalletAccounts/setUnverifiedWalletAccounts';
/** @category Extension Development */
export { formatSignInMessage } from '../modules/wallets/utils/formatSignInMessage';
/** @category Extension Development */
export { formatWalletAccountId } from '../modules/wallets/utils/formatWalletAccountId';
/** @category Extension Development */
export { formatWalletProviderGroupKey } from '../modules/wallets/utils/formatWalletProviderGroupKey';
/** @category Extension Development */
export { formatWalletProviderKey } from '../modules/wallets/utils/formatWalletProviderKey';
/** @category Extension Development */
export { getAllProvidersFromWindow } from '../modules/wallets/utils/getAllProvidersFromWindow';
/** @category Extension Development */
export { getInjectedProviderFromWindow } from '../modules/wallets/utils/getInjectedProviderFromWindow';
/** @category Extension Development */
export { getVerifiedCredentialForWalletAccount } from '../modules/wallets/utils/getVerifiedCredentialForWalletAccount';
/** @category Extension Development */
export { getWalletProviderFromWalletAccount } from '../modules/wallets/utils/getWalletProviderFromWalletAccount';
/** @category Extension Development */
export { normalizeWalletNameWithChain } from '../modules/wallets/utils/normalizeWalletNameWithChain';
/** @category Extension Development */
export { verifyMessageSignatureOwnership } from '../modules/wallets/verifyMessageSignatureOwnership';
/** @category Extension Development */
export type { BaseWalletAccount } from '../modules/wallets/walletAccount';
/** @category Extension Development */
export type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
  TransferAmountParams,
  WalletProvider,
  WalletProviderConnectAddress,
} from '../modules/wallets/walletProvider';
/** @category Extension Development */
export { getActiveNetworkIdFromLastKnownRegistry } from '../modules/wallets/walletProvider/defaultImplementations/getActiveNetworkIdFromLastKnownRegistry';
/** @category Extension Development */
export { switchActiveNetworkInLastKnownRegistry } from '../modules/wallets/walletProvider/defaultImplementations/switchActiveNetworkInLastKnownRegistry';
/** @category Extension Development */
export { createWalletProviderEventEmitter } from '../modules/wallets/walletProvider/events/createWalletProviderEventEmitter';
/** @category Extension Development */
export { splitWalletProviderKey } from '../modules/wallets/walletProvider/splitWalletProviderKey';
/** @category Extension Development */
export {
  getWalletProviderRegistry,
  WalletProviderPriority,
} from '../modules/wallets/walletProviderRegistry';
/** @category Extension Development */
export type {
  AsyncProcessEntry,
  AsyncProcessStatus,
  AsyncTrack,
  AsyncTrackEmitter,
} from '../services/asyncTrack/asyncTrack.types';
/** @category Extension Development */
export { createCrossTabBroadcast } from '../services/crossTabBroadcast/createCrossTabBroadcast';
/** @category Extension Development */
export type {
  CrossTabBroadcast,
  CrossTabBroadcastMessage,
} from '../services/crossTabBroadcast';
/** @category Extension Development */
export { CrossTabBroadcastMessageSchema } from '../services/crossTabBroadcast';
/** @category Extension Development */
export { createLogger } from '../services/logger/createLogger';
/** @category Extension Development */
export type { Logger, LogLevel } from '../services/logger/logger.types';
/** @category Extension Development */
export type { PasskeyService } from '../services/passkey';
/** @category Extension Development */
export {
  createRuntimeServiceAccessKey,
  type RuntimeServices,
} from '../services/runtimeServices';
/** @category Extension Development */
export {
  createLocalStorageAdapter,
  createStorage,
  createStorageKeySchema,
  type Storage,
  type StorageAdapter,
  type StorageKeySchema,
} from '../services/storage';
/** @category Extension Development */
export type { ExtendParameters } from '../types/ExtendParameters';
/** @category Extension Development */
export { assertDefined } from '../utils/assertDefined';
/** @category Extension Development */
export { bufferToBase64 } from '../utils/bufferToBase64';
/** @category Extension Development */
export type { DebouncedMutex } from '../utils/debouncedMutex/debouncedMutex.types';
/** @category Extension Development */
export { filterDuplicates } from '../utils/filterDuplicates';
/** @category Extension Development */
export { getBuffer } from '../utils/getBuffer';
/** @category Extension Development */
export { getChainFromVerifiedCredentialChain } from '../utils/getChainFromVerifiedCredentialChain';
/** @category Extension Development */
export { getNonce } from '../utils/getNonce';
/** @category Extension Development */
export { parseAmountToSmallestUnit } from '../utils/parseAmountToSmallestUnit';
/** @category Extension Development */
export type { ObservableState } from '../utils/observable/observable.types';
/** @category Extension Development */
export { createSignInMessageStatement } from '../modules/wallets/utils/getSignInMessage/createSignInMessageStatement';
/** @category Extension Development */
export { subscribeWithSelector } from '../utils/observable/subscribeWithSelector';
/** @category Extension Development */
export { updateWalletProviderKeysForVerifiedCredentials } from '../modules/wallets/walletProvider/walletProviderKeyMap/updateWalletProviderKeysForVerifiedCredentials';
/** @category Extension Development */
export { DYNAMIC_ICONIC_SPRITE_URL } from '../constants';
/** @category Extension Development */
export { createDeferredPromise } from '../utils/deferredPromise';
/** @category Extension Development */
export { getDeviceSigner } from '../modules/deviceRegistration/getDeviceSigner';
/** @category Extension Development */
export { getHeadersForNonceSignedByDeviceSigners } from '../modules/deviceRegistration/getHeadersForNonceSignedByDeviceSigners';
/** @category Extension Development */
export { createDeviceSignatureHeadersMiddleware } from '../modules/apiClient/utils/deviceSignatureHeadersMiddleware/createDeviceSignatureHeadersMiddleware';
/** @category Extension Development */
export { randomString } from '../utils/randomString';
